import { useCallback, useState } from 'react';

import LinkIcon from '@atlaskit/icon/core/link';

import { Button } from '@/libraries/design/components/button/src/button';

import { useSelector } from '../context/hooks';
import { selectShareableQuery } from '../context/selectors';
import { toQueryString } from '../util/url';

/**
 * Copy-URL-to-share button for the Estimate step.
 *
 * Uses the WAC design-library `Button` (secondary, small) with the atlaskit
 * `link` icon before the label — matching Figma node 1103:32350 where the
 * share affordance sits alongside a Back button on the bottom action row of
 * the Estimate step.
 */
export const ShareButton = () => {
	const query = useSelector(selectShareableQuery);
	const [copied, setCopied] = useState(false);

	const onShare = useCallback(async () => {
		const url = buildShareUrl(toQueryString(query));
		try {
			await navigator.clipboard.writeText(url);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// Clipboard may be unavailable (e.g. insecure context) — fall back.
			window.prompt('Copy this URL:', url);
		}
	}, [query]);

	return (
		<Button variant="primary" size="small" onClick={onShare}>
			<span
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: '8px',
				}}
			>
				<LinkIcon label="" />
				{copied ? 'Link copied' : 'Copy URL to share'}
			</span>
		</Button>
	);
};

const buildShareUrl = (queryString: string): string => {
	const { origin, pathname } = window.location;
	return `${origin}${pathname}${queryString ? `?${queryString}` : ''}`;
};
